/**
 * CRM-lite sales contacts (PE-2159 FE, backend in order-service's `sales` schema, proxied by
 * osprey-api `/sales/contacts`). Mirrors order-service's SalesContact / SalesContactActivity
 * models (prisma/sales/schema/sales.model.prisma).
 */

export const SalesContactStage = {
  PROSPECT: 'PROSPECT',
  IN_CONVERSATION: 'IN_CONVERSATION',
  QUOTED: 'QUOTED',
  SIGNED: 'SIGNED',
  ACTIVE_MERCHANT: 'ACTIVE_MERCHANT',
  LOST: 'LOST',
} as const;
export type SalesContactStage = (typeof SalesContactStage)[keyof typeof SalesContactStage];

export const SalesContactBuildStatus = {
  NONE: 'NONE',
  FIRST_DRAFT: 'FIRST_DRAFT',
  DESIGN_IN_REVIEW: 'DESIGN_IN_REVIEW',
  DESIGNED: 'DESIGNED',
  DEPLOYED: 'DEPLOYED',
} as const;
export type SalesContactBuildStatus =
  (typeof SalesContactBuildStatus)[keyof typeof SalesContactBuildStatus];

export const SalesContactSource = {
  PROSPECT_SHEET_IMPORT: 'PROSPECT_SHEET_IMPORT',
  MANUAL: 'MANUAL',
  SALES_TOOL: 'SALES_TOOL',
  LEAD_INTAKE_API: 'LEAD_INTAKE_API',
} as const;
export type SalesContactSource = (typeof SalesContactSource)[keyof typeof SalesContactSource];

export const SalesContactActivityType = {
  NOTE: 'NOTE',
  STAGE_CHANGE: 'STAGE_CHANGE',
  INTAKE_UPDATE: 'INTAKE_UPDATE',
} as const;
export type SalesContactActivityType =
  (typeof SalesContactActivityType)[keyof typeof SalesContactActivityType];

export interface SalesContactActivity {
  id: string;
  contactId: string;
  activityType: SalesContactActivityType;
  body: string;
  createdById: string | null;
  createdAt: string;
}

export interface SalesContact {
  id: string;
  name: string;
  businessName: string | null;
  role: string | null;
  /** Unique across contacts — a duplicate comes back as a 409. */
  email: string | null;
  phone: string | null;
  /** Also unique across contacts. */
  igHandle: string | null;
  otherHandles: string[];
  website: string | null;
  source: SalesContactSource;
  stage: SalesContactStage;
  buildStatus: SalesContactBuildStatus;
  solutionInterest: string | null;
  category: string | null;
  primaryAudience: string | null;
  visualQualityScore: number | null;
  growthMindsetScore: number | null;
  hasCelebrityOrPressAttention: boolean;
  igFollowerCount: number | null;
  assetsFolderUrl: string | null;
  designLinkUrl: string | null;
  assignedRepId: string | null;
  merchantId: string | null;
  referredByContactId: string | null;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

/** `GET /sales/contacts/:id` includes the activity history, newest first. */
export interface SalesContactDetail extends SalesContact {
  activities?: SalesContactActivity[];
}

/**
 * ⚠️ `stage` and `buildStatus` are ABSENT from order-service's CreateSalesContactDto — a new
 * contact always starts PROSPECT/NONE. They are update-only, so the create form must not
 * offer them (sending them would be stripped at best, rejected at worst).
 */
export interface CreateSalesContactPayload {
  name: string;
  businessName?: string;
  role?: string;
  email?: string;
  phone?: string;
  igHandle?: string;
  otherHandles?: string[];
  /** @IsUrl server-side — a bare domain is rejected with a 400. */
  website?: string;
  source?: SalesContactSource;
  solutionInterest?: string;
  category?: string;
  primaryAudience?: string;
  visualQualityScore?: number;
  growthMindsetScore?: number;
  hasCelebrityOrPressAttention?: boolean;
  igFollowerCount?: number;
  /** @IsUrl server-side. */
  assetsFolderUrl?: string;
  /** @IsUrl server-side. */
  designLinkUrl?: string;
  assignedRepId?: string;
  merchantId?: string;
  referredByContactId?: string;
}

/**
 * Everything creatable, plus the two lifecycle fields and the invoice link. EVERY field is
 * optional — order-service's UpdateSalesContactDto marks all of them `@IsOptional()`, so a
 * one-field PATCH (moving the stage along, say) sends exactly that one field.
 */
export type UpdateSalesContactPayload = Partial<CreateSalesContactPayload> & {
  stage?: SalesContactStage;
  buildStatus?: SalesContactBuildStatus;
  /** Links an existing, unclaimed sales invoice to this contact. */
  salesInvoiceId?: string;
};

/** Every filter order-service's SalesContactQueryDto accepts. Note: NO page/limit — see below. */
export interface SalesContactQuery {
  stage?: SalesContactStage;
  buildStatus?: SalesContactBuildStatus;
  source?: SalesContactSource;
  solutionInterest?: string;
  assignedRepId?: string;
  /** Case-insensitive substring match against name, businessName and email. */
  q?: string;
}

export interface CreateSalesContactActivityPayload {
  body: string;
  activityType?: SalesContactActivityType;
}

export const STAGE_LABELS: Record<SalesContactStage, string> = {
  PROSPECT: 'Prospect',
  IN_CONVERSATION: 'In conversation',
  QUOTED: 'Quoted',
  SIGNED: 'Signed',
  ACTIVE_MERCHANT: 'Active merchant',
  LOST: 'Lost',
};

export const STAGE_CLASSES: Record<SalesContactStage, string> = {
  PROSPECT: 'bg-muted text-muted-foreground',
  IN_CONVERSATION: 'bg-blue-100 text-blue-700',
  QUOTED: 'bg-amber-100 text-amber-800',
  SIGNED: 'bg-green-100 text-green-800',
  ACTIVE_MERCHANT: 'bg-green-100 text-green-800',
  LOST: 'bg-red-100 text-red-800',
};

export const BUILD_STATUS_LABELS: Record<SalesContactBuildStatus, string> = {
  NONE: 'Not started',
  FIRST_DRAFT: 'First draft',
  DESIGN_IN_REVIEW: 'Design in review',
  DESIGNED: 'Designed',
  DEPLOYED: 'Deployed',
};

export const SOURCE_LABELS: Record<SalesContactSource, string> = {
  PROSPECT_SHEET_IMPORT: 'Prospect sheet import',
  MANUAL: 'Manual',
  SALES_TOOL: 'Sales tool',
  LEAD_INTAKE_API: 'Lead intake API',
};

export const ACTIVITY_TYPE_LABELS: Record<SalesContactActivityType, string> = {
  NOTE: 'Note',
  STAGE_CHANGE: 'Stage change',
  INTAKE_UPDATE: 'Intake update',
};
